import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DirhamSymbol } from 'dirham/react';
import 'dirham/css';

const TiltCard = ({ children, className, style }) => {
  const [tiltStyle, setTiltStyle] = useState({});
  
  const handleMouseMove = (e) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - left) / width;
    const y = (e.clientY - top) / height;
    const rotateX = (0.5 - y) * 15; // Max 15 degrees tilt
    const rotateY = (x - 0.5) * 15;
    setTiltStyle({
      transform: `perspective(1200px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`,
      transition: 'transform 0.1s ease-out'
    });
  };
  
  const handleMouseLeave = () => {
    setTiltStyle({
      transform: 'perspective(1200px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)',
      transition: 'transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1)'
    });
  };

  return (
    <div 
      onMouseMove={handleMouseMove} 
      onMouseLeave={handleMouseLeave} 
      style={{ ...style, ...tiltStyle, transformStyle: 'preserve-3d' }}
      className={className}
    >
      {children}
    </div>
  );
};

const ScrollReveal = ({ children, className = '', delay = 0, style = {} }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return (
    <div 
      ref={ref} 
      className={`${className} reveal-hidden ${isVisible ? 'reveal-visible' : ''}`} 
      style={{ ...style, transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const TizzaLandingPage = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Trigger initial load animations
    setTimeout(() => setIsLoaded(true), 100);

    const handleScroll = () => {
      setScrollY(window.scrollY);
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    // Use requestAnimationFrame for smoother scroll updates
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll);
    
    // Add fonts if not already present
    if (!document.getElementById('tizza-fonts')) {
      const link = document.createElement('link');
      link.id = 'tizza-fonts';
      link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@300;400;500;600&family=Outfit:wght@300;400;700;800&display=swap';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const themeColors = {
    green: '#2f3f22',
    beige: '#e3cca3',
    beigeDark: '#cbb287'
  };

  const menuItems = [
    {
      id: 1,
      name: 'Margherita Classico',
      description: 'San Marzano tomato sauce, fresh mozzarella, hand-torn basil, and a drizzle of single-estate extra virgin olive oil.',
      image: '/images/tizza/margherita.jpg',
      price: <><DirhamSymbol /> 59</>,
      badge: 'Best Seller'
    },
    {
      id: 2,
      name: 'Spicy Pepperoni',
      description: 'Double artisanal pepperoni, hot honey drizzle, chili flakes, and our signature three-cheese blend.',
      image: '/images/tizza/pepperoni.jpg',
      price: <><DirhamSymbol /> 69</>
    },
    {
      id: 3,
      name: 'Truffle Bianca',
      description: 'Roasted wild mushrooms, truffle cream, fior di latte, thyme, and white truffle oil.',
      image: '/images/tizza/hero.jpg',
      price: <><DirhamSymbol /> 85</>,
      badge: "Chef's Special"
    },
    {
      id: 4,
      name: 'Burrata & Prosciutto',
      description: 'Thinly sliced prosciutto di Parma, fresh creamy burrata, arugula, and balsamic glaze.',
      image: '/images/tizza/margherita.jpg',
      price: <><DirhamSymbol /> 89</>
    },
    {
      id: 5,
      name: 'Pistachio & Stracciatella',
      description: 'Hand-crushed Sicilian pistachios, creamy stracciatella di bufala, mortadella, and fresh lemon zest.',
      image: '/images/tizza/hero.jpg',
      price: <><DirhamSymbol /> 92</>,
      badge: 'New'
    },
    {
      id: 6,
      name: 'Smoked Provola & Nduja',
      description: 'Spicy Calabrian nduja sausage, smoked provola cheese, roasted garlic, and organic hot honey.',
      image: '/images/tizza/pepperoni.jpg',
      price: <><DirhamSymbol /> 75</>
    }
  ];

  const staticMenuCategories = [
    {
      title: "Starters & Salads",
      items: [
        { name: "Wood-Fired Garlic Bread", price: <><DirhamSymbol /> 35</>, desc: "Artisanal sourdough, confit garlic, parmesan", image: "/images/tizza/margherita.jpg" },
        { name: "Burrata Caprese", price: <><DirhamSymbol /> 55</>, desc: "Fresh burrata, heirloom tomatoes, basil pesto", image: "/images/tizza/pepperoni.jpg" },
        { name: "Truffle Arancini", price: <><DirhamSymbol /> 45</>, desc: "Crispy risotto balls, wild mushroom, truffle aioli", image: "/images/tizza/hero.jpg" }
      ]
    },
    {
      title: "Wood-Fired Pizzas",
      items: [
        { name: "Margherita Classico", price: <><DirhamSymbol /> 59</>, desc: "San Marzano tomatoes, fresh mozzarella, basil", image: "/images/tizza/margherita.jpg" },
        { name: "Spicy Pepperoni", price: <><DirhamSymbol /> 69</>, desc: "Double pepperoni, hot honey, chili flakes", image: "/images/tizza/pepperoni.jpg" },
        { name: "Truffle Bianca", price: <><DirhamSymbol /> 85</>, desc: "Wild mushrooms, truffle cream, fior di latte", image: "/images/tizza/hero.jpg" },
        { name: "Quattro Formaggi", price: <><DirhamSymbol /> 75</>, desc: "Mozzarella, gorgonzola, fontina, parmesan", image: "/images/tizza/margherita.jpg" }
      ]
    },
    {
      title: "Pastas",
      items: [
        { name: "Pappardelle Ragù", price: <><DirhamSymbol /> 85</>, desc: "Slow-cooked beef ragù, fresh pappardelle, parmesan", image: "/images/tizza/pepperoni.jpg" },
        { name: "Truffle Cacio e Pepe", price: <><DirhamSymbol /> 95</>, desc: "Bucatini, pecorino romano, black pepper, fresh truffle", image: "/images/tizza/hero.jpg" },
        { name: "Spaghetti Vongole", price: <><DirhamSymbol /> 98</>, desc: "Fresh clams, white wine, garlic, chili, parsley", image: "/images/tizza/margherita.jpg" }
      ]
    },
    {
      title: "Desserts & Drinks",
      items: [
        { name: "Classic Tiramisu", price: <><DirhamSymbol /> 40</>, desc: "Espresso-soaked ladyfingers, mascarpone cream", image: "/images/tizza/hero.jpg" },
        { name: "Cannoli Siciliani", price: <><DirhamSymbol /> 35</>, desc: "Crispy pastry, sweet ricotta, pistachio, chocolate", image: "/images/tizza/margherita.jpg" },
        { name: "Artisanal Gelato", price: <><DirhamSymbol /> 25</>, desc: "Vanilla bean, pistachio, or dark chocolate", image: "/images/tizza/pepperoni.jpg" }
      ]
    }
  ];

  return (
    <div style={{ backgroundColor: themeColors.green, color: themeColors.beige, minHeight: '100vh', fontFamily: "'Inter', sans-serif", overflowX: 'hidden' }}>
      
      {/* Custom Styles for Animations & Professional Design */}
      <style>
        {`
          @keyframes fadeInUp3D {
            from { 
              opacity: 0; 
              transform: translateY(40px) rotateX(-15deg) scale(0.95); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0) rotateX(0deg) scale(1); 
            }
          }
          .animate-fade-in-up {
            opacity: 0;
            transform-origin: center bottom;
            animation: fadeInUp3D 1.2s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
          }
          .delay-100 { animation-delay: 0.1s; }
          .delay-200 { animation-delay: 0.2s; }
          .delay-300 { animation-delay: 0.3s; }
          .delay-400 { animation-delay: 0.4s; }
          .delay-500 { animation-delay: 0.5s; }
          
          .reveal-hidden {
            opacity: 0;
            transform: translateY(40px) rotateX(-10deg);
            transition: all 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
            transform-origin: center bottom;
          }
          .reveal-visible {
            opacity: 1;
            transform: translateY(0) rotateX(0deg);
          }
          
          .glass-panel {
            background: rgba(227, 204, 163, 0.02);
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
            border: 1px solid rgba(227, 204, 163, 0.08);
            transform-style: preserve-3d;
          }
          
          .hover-glow {
            transition: all 0.4s ease;
          }
          .hover-glow:hover {
            box-shadow: 0 0 30px rgba(227, 204, 163, 0.3);
            transform: translateY(-2px);
          }
          
          .btn-outline {
            position: relative;
            overflow: hidden;
            transition: all 0.4s ease;
          }
          .btn-outline::before {
            content: '';
            position: absolute;
            top: 0; left: 0; width: 100%; height: 100%;
            background: ${themeColors.beige};
            transform: scaleX(0);
            transform-origin: right;
            transition: transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
            z-index: -1;
          }
          .btn-outline:hover::before {
            transform: scaleX(1);
            transform-origin: left;
          }
          .btn-outline:hover {
            color: ${themeColors.green} !important;
          }
        `}
      </style>

      {/* Navbar */}
      <nav 
        className="fixed w-full z-50 transition-all duration-500 ease-in-out"
        style={{ 
          padding: isScrolled ? '1rem 2rem' : '2.5rem 2rem',
          backgroundColor: isScrolled ? 'rgba(47, 63, 34, 0.95)' : 'transparent',
          backdropFilter: isScrolled ? 'blur(15px)' : 'none',
          borderBottom: isScrolled ? '1px solid rgba(227, 204, 163, 0.1)' : '1px solid transparent'
        }}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className={`flex flex-col items-center cursor-pointer transition-opacity duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0'}`} onClick={() => window.scrollTo(0, 0)}>
            <img src="/images/tizza/tizza-logo.png" alt="Tizza & Co. Logo" className="h-16 w-auto object-contain drop-shadow-md" />
          </div>
          
          <div className={`hidden md:flex space-x-10 text-xs uppercase tracking-[0.2em] font-medium transition-opacity duration-700 delay-100 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
            <a href="#story" className="relative hover:opacity-100 opacity-70 transition-opacity after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-[1px] after:bg-[#e3cca3] hover:after:w-full after:transition-all after:duration-300">Our Story</a>
            <a href="#menu" className="relative hover:opacity-100 opacity-70 transition-opacity after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-[1px] after:bg-[#e3cca3] hover:after:w-full after:transition-all after:duration-300">Menu</a>
            <a href="#location" className="relative hover:opacity-100 opacity-70 transition-opacity after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-[1px] after:bg-[#e3cca3] hover:after:w-full after:transition-all after:duration-300">Location</a>
          </div>
          
          <div className={`transition-opacity duration-700 delay-200 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
            <button 
              onClick={() => navigate('/tizza')}
              className="px-7 py-2.5 rounded-full text-xs uppercase tracking-[0.2em] font-semibold hover-glow"
              style={{ backgroundColor: themeColors.beige, color: themeColors.green }}
            >
              Order Now
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden perspective" style={{ perspective: '1000px' }}>
        {/* Background Image with Parallax */}
        <div 
          className="absolute inset-0 z-0 ease-out"
          style={{
            backgroundImage: "url('/images/tizza/hero.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateZ(-100px) scale(1.1) translateY(${scrollY * 0.4}px)`,
          }}
        >
          {/* Moody Overlay with Vignette */}
          <div className="absolute inset-0" style={{ 
            background: 'radial-gradient(circle at center, rgba(47, 63, 34, 0.4) 0%, rgba(47, 63, 34, 0.85) 100%)' 
          }}></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#2f3f22] via-transparent to-transparent opacity-90"></div>
        </div>

        {/* Hero Content with Inverse Parallax */}
        <div 
          className="relative z-10 text-center px-4 max-w-5xl mx-auto flex flex-col items-center mt-20" 
          style={{ 
            transformStyle: 'preserve-3d',
            transform: `translateY(${scrollY * 0.15}px)` // Subtly follows the scroll downwards
          }}
        >
          {isLoaded && (
            <>
              <div className="flex items-center space-x-4 mb-8 animate-fade-in-up" style={{ transform: 'translateZ(20px)' }}>
                <div className="h-[1px] w-12 bg-[#e3cca3] opacity-60"></div>
                <p className="uppercase tracking-[0.4em] text-xs md:text-sm font-medium opacity-90">Welcome to</p>
                <div className="h-[1px] w-12 bg-[#e3cca3] opacity-60"></div>
              </div>
              
              <h2 
                className="text-6xl md:text-8xl lg:text-9xl font-bold mb-8 uppercase animate-fade-in-up delay-100" 
                style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.02em', lineHeight: '0.95', transform: 'translateZ(50px)' }}
              >
                Crafted for <br/>
                <span className="italic font-light lowercase" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '0' }}>craving.</span>
              </h2>
              
              <p className="text-base md:text-xl mb-12 max-w-2xl mx-auto opacity-80 font-light leading-relaxed animate-fade-in-up delay-200" style={{ transform: 'translateZ(30px)' }}>
                Experience the perfect harmony of 72-hour artisanal dough, premium curated ingredients, and wood-fired passion.
              </p>
              
              <div className="flex flex-col sm:flex-row space-y-5 sm:space-y-0 sm:space-x-6 animate-fade-in-up delay-300" style={{ transform: 'translateZ(40px)' }}>
                <button 
                  onClick={() => navigate('/tizza')}
                  className="px-10 py-4 rounded-full text-sm uppercase tracking-[0.2em] font-semibold hover-glow"
                  style={{ backgroundColor: themeColors.beige, color: themeColors.green }}
                >
                  Explore Menu
                </button>
                <button 
                  className="btn-outline px-10 py-4 rounded-full text-sm uppercase tracking-[0.2em] font-semibold border border-[#e3cca3] z-10"
                  style={{ color: themeColors.beige }}
                >
                  Book a Table
                </button>
              </div>
            </>
          )}
        </div>
        
        {/* Scroll Indicator */}
        <div className={`absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10 transition-opacity duration-1000 delay-500 ${isLoaded ? 'opacity-70' : 'opacity-0'} animate-bounce`}>
          <a href="#story">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={themeColors.beige} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M19 12l-7 7-7-7"/>
            </svg>
          </a>
        </div>
      </section>

      {/* Story Section */}
      <section id="story" className="py-32 px-6 relative">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-20">
          <ScrollReveal className="lg:w-1/2 relative group" delay={0}>
            {/* Tilt Wrapper for the image block */}
            <TiltCard className="w-full relative" style={{ perspective: '1000px' }}>
              {/* Decorative element with downward parallax */}
              <div 
                className="absolute -inset-4 border border-[#e3cca3] opacity-20 transform -rotate-3 transition-transform duration-700 group-hover:rotate-0" 
                style={{ transform: `translateZ(-20px) rotate(-3deg) translateY(${(scrollY - 600) * 0.08}px)` }}
              ></div>
              
              {/* Image container */}
              <div className="relative p-2 bg-[#e3cca3]" style={{ transform: 'translateZ(10px)' }}>
                <img src="/images/tizza/hero.jpg" alt="Wood-fired oven" className="w-full h-[500px] object-cover filter grayscale contrast-125 transition-all duration-700 group-hover:grayscale-0 group-hover:contrast-100" />
              </div>
              
              {/* Overlay badge with upward parallax */}
              <div 
                className="absolute -bottom-8 -right-8 glass-panel p-6 rounded-full w-32 h-32 flex flex-col items-center justify-center shadow-2xl z-10"
                style={{ transform: `translateZ(60px) translateY(${(scrollY - 600) * -0.05}px)` }}
              >
                <span className="text-3xl font-bold font-serif italic" style={{ transform: 'translateZ(20px)' }}>100%</span>
                <span className="text-xs uppercase tracking-widest mt-1 text-center" style={{ transform: 'translateZ(20px)' }}>Wood<br/>Fired</span>
              </div>
            </TiltCard>
          </ScrollReveal>
          
          <ScrollReveal className="lg:w-1/2 space-y-8" delay={200} style={{ transform: `translateY(${(scrollY - 600) * -0.03}px)` }}>
            <div className="flex items-center space-x-4 mb-2">
              <div className="h-[1px] w-8 bg-[#e3cca3] opacity-60"></div>
              <h3 className="text-xs uppercase tracking-[0.3em] font-semibold opacity-80" style={{ color: themeColors.beige }}>Our Story</h3>
            </div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold uppercase leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              More than just <br/><span className="italic font-light lowercase">a pizzeria.</span>
            </h2>
            
            <div className="space-y-6 text-lg font-light opacity-80 leading-relaxed max-w-xl">
              <p>
                At Tizza & Co., we believe that pizza is an art form. We spend days perfecting our slow-fermented dough, sourcing the finest local ingredients, and mastering the fire to bring you a culinary experience that satisfies your deepest cravings.
              </p>
              <p>
                Every pie that leaves our oven is a testament to our dedication. It's not fast food. It's crafted food, made for those who appreciate the details.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Menu Highlights Section */}
      <section id="menu" className="py-32 px-6 relative border-y border-[#e3cca3]/10 overflow-hidden" style={{ backgroundColor: 'rgba(227, 204, 163, 0.02)' }}>
        
        {/* Parallax Background Text Watermark */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0 flex items-center justify-center">
          <div 
            className="absolute font-bold opacity-[0.015] whitespace-nowrap text-[#e3cca3]" 
            style={{ 
              fontSize: '25rem', 
              fontFamily: "'Bebas Neue', sans-serif", 
              transform: `translateX(${(scrollY - 1200) * -0.3}px)` 
            }}
          >
            TIZZA & CO. ARTISANAL
          </div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <ScrollReveal className="text-center mb-24" delay={0}>
            <div className="flex justify-center items-center space-x-4 mb-4">
              <div className="h-[1px] w-12 bg-[#e3cca3] opacity-40"></div>
              <h3 className="text-xs uppercase tracking-[0.4em] font-semibold opacity-80" style={{ color: themeColors.beige }}>Craving Satisfiers</h3>
              <div className="h-[1px] w-12 bg-[#e3cca3] opacity-40"></div>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>Signature Pies</h2>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {menuItems.map((item, index) => (
              <div 
                key={item.id} 
                className="group relative w-full rounded-3xl overflow-hidden cursor-pointer shadow-2xl transition-all duration-500 hover:shadow-xl" 
                style={{ 
                  height: '400px',
                  backgroundColor: '#2f3f22',
                  marginTop: (index % 2 === 1) ? '24px' : '0px' 
                }}
                onClick={() => navigate('/tizza')}
              >
                  {/* Premium Badge */}
                  {item.badge && (
                    <div 
                      className="absolute top-4 right-4 z-20 text-xs uppercase font-bold px-3 py-1 rounded-full shadow-lg"
                      style={{ backgroundColor: '#e3cca3', color: '#2f3f22', letterSpacing: '0.1em' }}
                    >
                      {item.badge}
                    </div>
                  )}

                  {/* Image Section */}
                  <div className="absolute top-0 left-0 w-full overflow-hidden" style={{ height: '60%' }}>
                    <img 
                      src={item.image} 
                      alt={item.name} 
                      className="w-full h-full object-cover transition-transform duration-1000 ease-out transform group-hover:scale-110"
                    />
                    <div className="absolute inset-0 transition-opacity duration-500 group-hover:opacity-0" style={{ backgroundImage: 'linear-gradient(to bottom, rgba(0,0,0,0.2), transparent, rgba(47,63,34,0.5))' }}></div>
                  </div>
                  
                  {/* Solid/Frosted Text Section */}
                  <div className="absolute bottom-0 left-0 w-full flex flex-col justify-between rounded-t-2xl p-6 transition-all duration-500 z-10" style={{ height: '45%', backgroundColor: 'rgba(43, 56, 32, 0.98)', borderTop: '1px solid rgba(227, 204, 163, 0.3)' }}>
                    <div>
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="text-2xl font-bold uppercase pr-2 leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.name}</h4>
                        <span className="text-xl font-light italic" style={{ color: '#e3cca3' }}>{item.price}</span>
                      </div>
                      <div className="h-px w-12 mb-4 opacity-70" style={{ backgroundImage: 'linear-gradient(to right, #e3cca3, transparent)' }}></div>
                      <p className="font-light text-sm opacity-80 overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                        {item.description}
                      </p>
                    </div>
                    
                    <div className="flex items-center text-xs uppercase font-semibold opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0" style={{ color: '#e3cca3', letterSpacing: '0.2em' }}>
                      Add to Order
                      <svg className="w-4 h-4 ml-2 transition-transform duration-300 transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                    </div>
                  </div>
              </div>
            ))}
          </div>
          
          {/* Static Elegant Text Menu */}
          <div className="mt-40 mb-20">
            <ScrollReveal className="text-center mb-16" delay={0}>
              <h2 className="text-4xl md:text-5xl font-bold uppercase tracking-wide" style={{ fontFamily: "'Outfit', sans-serif" }}>Full Menu</h2>
              <div className="h-[1px] w-24 bg-[#e3cca3] opacity-40 mx-auto mt-6"></div>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-20 gap-y-16">
              {staticMenuCategories.map((category, idx) => (
                <ScrollReveal key={idx} delay={idx * 150} className="flex flex-col">
                  <h3 className="text-2xl uppercase tracking-[0.1em] font-semibold mb-8 text-[#e3cca3] border-b border-[#e3cca3]/20 pb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {category.title}
                  </h3>
                  <div className="space-y-6">
                    {category.items.map((item, itemIdx) => (
                      <div 
                        key={itemIdx} 
                        className="group flex flex-col sm:flex-row items-start sm:items-center p-4 rounded-2xl cursor-pointer transition-all duration-400 hover:shadow-xl hover:-translate-y-1"
                        style={{ backgroundColor: 'rgba(47,63,34, 0.3)', border: '1px solid rgba(227,204,163,0.05)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(47,63,34, 0.7)'; e.currentTarget.style.borderColor = 'rgba(227,204,163,0.3)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(47,63,34, 0.3)'; e.currentTarget.style.borderColor = 'rgba(227,204,163,0.05)'; }}
                      >
                        {/* Modern Thumbnail Image */}
                        <div className="w-full sm:w-24 h-48 sm:h-24 rounded-xl overflow-hidden flex-shrink-0 mb-4 sm:mb-0 sm:mr-6 shadow-md relative">
                          <img 
                            src={item.image} 
                            alt={item.name} 
                            className="w-full h-full object-cover transition-transform duration-700 transform group-hover:scale-110"
                          />
                          <div className="absolute inset-0 transition-opacity duration-300 group-hover:opacity-0" style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}></div>
                        </div>
                        
                        {/* Text Content */}
                        <div className="flex flex-col flex-grow w-full">
                          <div className="flex items-start justify-between w-full mb-1">
                            <h4 className="text-xl font-bold tracking-wide uppercase transition-colors duration-300 pr-4" style={{ fontFamily: "'Outfit', sans-serif" }}
                                onMouseEnter={(e) => e.currentTarget.style.color = '#e3cca3'}
                                onMouseLeave={(e) => e.currentTarget.style.color = ''}
                            >
                              {item.name}
                            </h4>
                            {/* Modern Price Badge */}
                            <div className="flex-shrink-0 px-4 py-1.5 rounded-full transition-colors duration-300" style={{ backgroundColor: 'rgba(227,204,163,0.1)', border: '1px solid rgba(227,204,163,0.2)' }}
                                 onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(227,204,163,0.2)'}
                                 onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(227,204,163,0.1)'}
                            >
                              <span className="text-lg font-medium" style={{ color: '#e3cca3' }}>{item.price}</span>
                            </div>
                          </div>
                          <p className="text-sm font-light opacity-70 leading-relaxed tracking-wide mt-2 sm:mt-1 max-w-[90%]">
                            {item.desc}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>

          <ScrollReveal className="text-center mt-20" delay={400}>
            <button 
              onClick={() => navigate('/tizza')}
              className="btn-outline px-12 py-4 text-sm uppercase tracking-[0.2em] font-semibold border border-[#e3cca3] inline-flex items-center group z-10"
              style={{ color: themeColors.beige }}
            >
              Order Online Now 
              <svg className="w-4 h-4 ml-3 transform transition-transform group-hover:translate-x-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
              </svg>
            </button>
          </ScrollReveal>
        </div>
      </section>

      {/* Footer */}
      <footer id="location" className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 mb-20">
          <ScrollReveal className="md:col-span-2 pr-10" delay={0}>
            <div className="flex flex-col items-start mb-8">
              <img src="/images/tizza/tizza-logo.png" alt="Tizza & Co. Logo" className="h-24 w-auto object-contain" />
            </div>
            <p className="opacity-60 max-w-md font-light leading-relaxed text-sm">
              Dedicated to the craft of exceptional pizza. Fermented slowly, topped thoughtfully, and fired precisely for the ultimate craving satisfaction.
            </p>
          </ScrollReveal>
          
          <ScrollReveal delay={150}>
            <h4 className="text-xs font-semibold mb-6 uppercase tracking-[0.2em] opacity-50">Visit Us</h4>
            <div className="space-y-2 opacity-80 font-light text-sm">
              <a 
                href="https://www.google.com/maps/place/Tizza%26co/@25.1512235,55.2477799,17z/data=!3m1!4b1!4m6!3m5!1s0x3e5f698267da0e55:0xda5e19a6a4e55d9a!8m2!3d25.1512187!4d55.2503548!16s%2Fg%2F11zhdg4c69?entry=ttu&g_ep=EgoyMDI2MDgxOS4wIKXMDSoASAFQAw%3D%3D" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:opacity-100 hover:text-[#e3cca3] transition-all cursor-pointer block"
              >
                Al Quoz 4, Dubai
              </a>
              <p>(near Star One Restaurant)</p>
              <p className="pt-4 hover:opacity-100 transition-opacity cursor-pointer">info@tizza.ae</p>
              <p className="hover:opacity-100 transition-opacity cursor-pointer">054 456 2040</p>
            </div>
          </ScrollReveal>
          
          <ScrollReveal delay={300}>
            <h4 className="text-xs font-semibold mb-6 uppercase tracking-[0.2em] opacity-50">Hours</h4>
            <div className="space-y-3 opacity-80 font-light text-sm">
              <div className="flex justify-between pb-2">
                <span>Everyday</span>
                <span>12 PM - 11:30 PM</span>
              </div>
            </div>
          </ScrollReveal>
        </div>
        
        <ScrollReveal className="flex flex-col md:flex-row justify-between items-center opacity-40 text-xs font-light pt-8 border-t border-[#e3cca3]/20" delay={400}>
          <p>&copy; {new Date().getFullYear()} Tizza & Co. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <a href="https://www.instagram.com/tizza.ae?igsi=dDNmemt0ZnIzOTB4" target="_blank" rel="noopener noreferrer" className="hover:opacity-100 transition-opacity">Instagram</a>
            <a href="#" className="hover:opacity-100 transition-opacity">Facebook</a>
            <a href="#" className="hover:opacity-100 transition-opacity">Privacy</a>
          </div>
        </ScrollReveal>
      </footer>
    </div>
  );
};

export default TizzaLandingPage;
